import axios from 'axios';
import { PAISES, coerceFromList } from "@/utils/paises";
import {
  getUserManagementLanguage,
  translateUserManagement,
} from "../i18n/userManagementI18n.js";
import { getEventFieldLabel } from "../i18n/eventFieldLabels.js";

const normalize = (v) => String(v ?? '').trim();
const uiT = (key, params = {}) => translateUserManagement(getUserManagementLanguage(), key, params);
const fieldName = (label, fields = []) => {
  const configured = (fields || []).find((field) => field?.label === label);
  return getEventFieldLabel(configured || { label }, getUserManagementLanguage()) || label || 'unknown';
};

const prepareServerErrors = (errors = [], fields = []) => (errors || []).map((error) => {
  const valueSuffix = error?.value ? `: ${error.value}` : '';
  const originalMessage = error?.message || error?.msg || '';
  return {
    ...error,
    message: getUserManagementLanguage() === 'en'
      ? uiT('validation.serverRejected', { field: fieldName(error?.field, fields), value: valueSuffix })
      : originalMessage,
  };
});

const getActiveFields = (camposObligatorios = []) =>
  (camposObligatorios || []).filter(f => f && f.activo && f.label);

const getRequiredFields = (camposObligatorios = []) =>
  getActiveFields(camposObligatorios).filter(f => f.requerido || f.mandatorio);

const isEmptyValue = (v) => {
  const s = normalize(v);
  return s === '' || s === '-- Seleccione --';
};

// Campos “sistema / UI” que NO deben contar para decidir si una fila está vacía
const NON_DATA_FIELDS = new Set([
  'staff_id', 'group_id', 'evento_id', 'escarapela', 'autorizado',
  'creado', 'editado', 'borrado',
  'isModified', 'isSaved'
]);

/**
 * Fila "vacía" = no tiene NINGÚN dato real en campos activos de captura.
 */
const isEffectivelyEmpty = (member, activeFields) => {
  const m = member || {};

  const meaningfulActive = (activeFields || [])
    .filter(f => f && f.label && !NON_DATA_FIELDS.has(f.label));

  if (meaningfulActive.length > 0) {
    return meaningfulActive.every(f => isEmptyValue(m?.[f.label]));
  }

  return Object.entries(m).every(([k, v]) => {
    if (NON_DATA_FIELDS.has(k)) return true;
    return isEmptyValue(v);
  });
};

const validateEmail = (email) => {
  const re = /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const sanitizeByType = (tipo, label, value) => {
  const raw = String(value ?? '');

  const t = String(tipo || '').toLowerCase();

  // NUMERO
  if (t === 'numero') {
    return raw.replace(/[^0-9]/g, '');
  }

  // DOCUMENTO (sin guion)
  if (label === 'numero_de_documento') {
    return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  // SANITIZADO (con guion medio)
  if (t === 'sanitizado') {
    return raw
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .replace(/-+/g, '-');
  }

  // EMAIL
  if (label === 'correo_electronico' || t === 'email') {
    return raw
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9@.\-_+]/g, '');
  }

  return raw;
};

// duplicates de mandatorios dentro del mismo batch (solo filas no vacías)
const checkForDuplicates = (members, camposObligatorios) => {
  const found = {};
  const activeFields = getActiveFields(camposObligatorios);
  const activeMand = activeFields.filter(c => c.mandatorio);

  const nonEmptyMembers = (members || []).filter(m => !isEffectivelyEmpty(m, activeFields));

  activeMand.forEach(campo => {
    const values = nonEmptyMembers
      .map(m => normalize(m?.[campo.label]))
      .filter(v => v !== '' && v !== '-- Seleccione --');

    const seen = new Set();
    for (const v of values) {
      if (seen.has(v)) {
        found[campo.label] = v;
        break;
      }
      seen.add(v);
    }
  });

  return found;
};

let progressModalFn = null;
let errorModalFn = null;
let lastValidationErrors = [];

function setProgressModal(fn) { progressModalFn = fn; }
function setErrorModal(fn) { errorModalFn = fn; }
function getLastValidationErrors() { return lastValidationErrors.slice(); }

/**
 * Devuelve batch de filas "tocadas" conservando índice original
 * [{ member, idx }]
 */
const getNonEmptyBatch = (members, activeFields) => {
  return (members || [])
    .map((member, idx) => ({ member, idx }))
    .filter(({ member }) => !isEffectivelyEmpty(member, activeFields));
};

const validateMembers = async (members, toast, camposObligatorios, validarMandatorio, eventoToken, tokenGrupo) => {
  lastValidationErrors = [];
  const activeFields = getActiveFields(camposObligatorios);
  const requiredFields = getRequiredFields(camposObligatorios);

  const erroresModal = [];

  // Duplicados batch (solo filas no vacías)
  const duplicates = checkForDuplicates(members, camposObligatorios);
  if (Object.keys(duplicates).length > 0) {
    for (let [field, value] of Object.entries(duplicates)) {
      erroresModal.push({
        row: "",
        field,
        message: uiT('validation.duplicate', { field: fieldName(field, activeFields), value })
      });
    }
  }

  // Validación por fila (solo filas no vacías)
  members.forEach((member, i) => {
    if (isEffectivelyEmpty(member, activeFields)) return;

  activeFields.forEach((f) => {
    member[f.label] = sanitizeByType(f.tipo_campo, f.label, member[f.label]);

    const ui = String(f.tipo_ui || "input").toLowerCase();

    // ✅ si es select, coerciona contra opciones (y valida)
    let opts = Array.isArray(f.opciones) ? f.opciones : [];
    if (f.label === "pais" && (!opts || !opts.length)) opts = PAISES;

    if (ui === "select" && opts.length) {
      const raw = normalize(member[f.label]);
      if (!raw) {
        member[f.label] = "";
        return;
      }

      const canon = coerceFromList(raw, opts);
      if (!canon) {
        erroresModal.push({
          row: i + 1,
          field: f.label,
          message: uiT('validation.invalidOption', { field: fieldName(f.label, activeFields) }),
        });
      } else {
        member[f.label] = canon; // ✅ aquí corrige "Mexico" -> "México" (o al revés según tu lista)
      }
    }
  });


    // requeridos
    requiredFields.forEach(f => {
      const v = normalize(member[f.label]);
      if (isEmptyValue(v)) {
        erroresModal.push({
          row: i + 1,
          field: f.label,
          message: uiT('validation.required', { field: fieldName(f.label, activeFields) })
        });
      }
    });

    // doc sanitizado
    if (member.numero_de_documento && !/^[A-Z0-9]+$/.test(String(member.numero_de_documento))) {
      erroresModal.push({
        row: i + 1,
        field: 'numero_de_documento',
        message: uiT('validation.documentFormat')
      });
    }

    // email
    if (member.correo_electronico && !validateEmail(member.correo_electronico)) {
      erroresModal.push({
        row: i + 1,
        field: 'correo_electronico',
        message: uiT('validation.invalidEmail', { email: member.correo_electronico })
      });
    }
  });

  if (erroresModal.length > 0) {
    lastValidationErrors = erroresModal.slice();
    if (errorModalFn) errorModalFn(erroresModal);
    else erroresModal.forEach(e => toast.warning(uiT('validation.recordPrefix', { row: e.row || '', message: e.message })));
    return false;
  }

  // Validación backend de mandatorios (si aplica) - solo filas no vacías
  if (validarMandatorio) {
    const mandFields = getActiveFields(camposObligatorios).filter(c => c.mandatorio);
    const errores = [];

    for (let i = 0; i < members.length; i++) {
      const m = members[i];
      if (isEffectivelyEmpty(m, activeFields)) continue;

      const payload = {};
      mandFields.forEach(c => {
        payload[c.label] = { valor: normalize(m[c.label]), staff_id: m.staff_id ?? null };
      });

      if (Object.values(payload).every(obj => !obj.valor)) continue;

      if (progressModalFn) progressModalFn(uiT('validation.reviewing', { current: i + 1, total: members.length }));

      try {
        const response = await axios.post(`/api/validar-mandatorios/${eventoToken}/${tokenGrupo}`, payload);
        if (!response.data.passed) {
          for (let [field, msg] of Object.entries(response.data.errors)) {
            errores.push({
              row: i + 1,
              field,
              message: getUserManagementLanguage() === 'en'
                ? uiT('validation.alreadyRegistered', { field: fieldName(field, activeFields), value: payload[field]?.valor || '' })
                : msg.replace(field, fieldName(field, activeFields))
            });
          }
        }
      } catch (e) {
        errores.push({ row: i + 1, field: 'unknown', message: uiT('validation.backendError') });
      }

      await new Promise(r => setTimeout(r, 120));
    }

    if (progressModalFn) progressModalFn('');

    if (errores.length > 0) {
      lastValidationErrors = errores.slice();
      if (errorModalFn) errorModalFn(errores);
      else errores.forEach(e => toast.warning(uiT('validation.recordPrefix', { row: e.row, message: e.message })));
      return false;
    }
  }

  return true;
};

const stripUiProps = (member) => {
  const rest = { ...(member || {}) };
  delete rest.isModified;
  delete rest.isSaved;
  return rest;
};

const markProcessedBatchAsSaved = (members, batch, ids) => {
  (ids || []).forEach((id, i) => {
    const originalIdx = batch[i]?.idx;
    if (originalIdx == null || !members?.[originalIdx]) return;

    if (id) members[originalIdx].staff_id = id;
    if (members[originalIdx].staff_id) {
      members[originalIdx].isModified = false;
      members[originalIdx].isSaved = true;
    }
  });
};

const auditFields = (options = {}) => ({
  Authorization: options.authToken || '',
  ...(options.audit?.id ? {
    audit_id: options.audit.id,
    audit_mode: options.audit.mode || null,
    audit_rows: Number(options.audit.rows || 0),
    audit_stats: options.audit.stats || {},
  } : {}),
});

const saveAllMembers = (group, toast, options = {}) => {
  axios.post(`/api/staffs/${group.group_id}`, {
    staff: group.staff_members.staff,
    conferencistas: group.staff_members.conferencistas,
    asistentes: group.staff_members.asistentes,
    ...auditFields(options),
  }).then(() => toast.success(uiT('save.allSuccess')))
    .catch(() => toast.error(uiT('save.membersError')));
};

const saveStaffMembers = async (group, toast, camposObligatorios, validarMandatorio, eventoToken, options = {}) => {
  lastValidationErrors = [];
  const active = getActiveFields(camposObligatorios);
  const batch = getNonEmptyBatch(group.staff_members.staff, active);

  if (batch.length === 0) {
    toast.info(uiT('save.noChanges'));
    return false;
  }

  const batchMembers = batch.map(x => x.member);

  if (!(await validateMembers(batchMembers, toast, camposObligatorios, validarMandatorio, eventoToken, group.group_id))) {
    return false;
  }

  const payload = batchMembers.map(m => stripUiProps(m));

  try {
    const response = await axios.post(`/api/staff/save-staff/${group.group_id}`, {
      staff: payload,
      ...auditFields(options),
    });

    const ids = response.data.processedIds || [];
    markProcessedBatchAsSaved(group.staff_members.staff, batch, ids);

    toast.success(uiT('save.success'));
    return true;
  } catch (error) {
    const data = error?.response?.data;
    if (error?.response?.status === 422 && data?.errors) {
      lastValidationErrors = prepareServerErrors(data.errors, camposObligatorios);
      if (errorModalFn) errorModalFn(lastValidationErrors);
    }
    toast.error(uiT('save.error'));
    return false;
  }
};

const saveConferencistas = async (group, toast, camposObligatorios, validarMandatorio, eventoToken, options = {}) => {
  lastValidationErrors = [];
  const active = getActiveFields(camposObligatorios);
  const batch = getNonEmptyBatch(group.staff_members.conferencistas, active);

  if (batch.length === 0) {
    toast.info(uiT('save.noChanges'));
    return false;
  }

  const batchMembers = batch.map(x => x.member);

  if (!(await validateMembers(batchMembers, toast, camposObligatorios, validarMandatorio, eventoToken, group.group_id))) {
    return false;
  }

  const payload = batchMembers.map(m => stripUiProps(m));

  try {
    const response = await axios.post(`/api/staff/save-conferencistas/${group.group_id}`, {
      conferencistas: payload,
      ...auditFields(options),
    });

    const ids = response.data.processedIds || [];
    markProcessedBatchAsSaved(group.staff_members.conferencistas, batch, ids);

    toast.success(uiT('save.success'));
    return true;
  } catch (error) {
    const data = error?.response?.data;
    if (error?.response?.status === 422 && data?.errors) {
      lastValidationErrors = prepareServerErrors(data.errors, camposObligatorios);
      if (errorModalFn) errorModalFn(lastValidationErrors);
    }
    toast.error(uiT('save.error'));
    return false;
  }
};

const saveAsistentes = async (group, toast, camposObligatorios, validarMandatorio, eventoToken, options = {}) => {
  lastValidationErrors = [];
  const active = getActiveFields(camposObligatorios);
  const batch = getNonEmptyBatch(group.staff_members.asistentes, active);

  if (batch.length === 0) {
    toast.info(uiT('save.noChanges'));
    return false;
  }

  const batchMembers = batch.map(x => x.member);

  if (!(await validateMembers(batchMembers, toast, camposObligatorios, validarMandatorio, eventoToken, group.group_id))) {
    return false;
  }

  const payload = batchMembers.map(m => stripUiProps(m));

  try {
    const response = await axios.post(`/api/staff/save-asistentes/${group.group_id}`, {
      asistentes: payload,
      ...auditFields(options),
    });

    const ids = response.data.processedIds || [];
    markProcessedBatchAsSaved(group.staff_members.asistentes, batch, ids);

    toast.success(uiT('save.success'));
    return true;
  } catch (error) {
    const data = error?.response?.data;
    if (error?.response?.status === 422 && data?.errors) {
      lastValidationErrors = prepareServerErrors(data.errors, camposObligatorios);
      if (errorModalFn) errorModalFn(lastValidationErrors);
    }
    toast.error(uiT('save.error'));
    return false;
  }
};

export {
  saveAllMembers,
  saveStaffMembers,
  saveConferencistas,
  saveAsistentes,
  setProgressModal,
  setErrorModal,
  getLastValidationErrors,
};
